// Batch G — Verdicts view.
//
// One row per deal showing verdict at asking → verdict at counter, with
// a colored "shift" pill (improved / same / degraded).
//
// rows = [{ deal: <DealPublic>, at_asking: {verdict}, at_counter: {verdict, verdict_improved_grades} }, ...]
//
// The pill is the headline UX of this whole page — it's what the user
// remembers when they scroll back through their book. So it's bold:
// large dot + label + delta number.

import { ArrowDown, ArrowRight, ArrowUp } from "lucide-react";
import VerdictBadge from "@/components/VerdictBadge";
import RoleDot from "@/components/deal/RoleDot";
// Phase 36 Batch Z follow-up — P1 fix: stage editor must be reachable
// from every Portfolio view-tab (Prices/Metrics/Verdicts), not just the
// Basic tab. Mirrors the inline pattern from Portfolio.jsx (lines 857-861).
import StageBadge from "@/components/pipeline/StageBadge";

function ShiftPill({ grade }) {
  if (grade > 0) {
    return (
      <span
        data-testid="verdict-shift-improved"
        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200"
      >
        <ArrowUp className="h-3 w-3" /> +{grade} improved
      </span>
    );
  }
  if (grade < 0) {
    return (
      <span
        data-testid="verdict-shift-degraded"
        className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200"
      >
        <ArrowDown className="h-3 w-3" /> {grade} degraded
      </span>
    );
  }
  return (
    <span
      data-testid="verdict-shift-same"
      className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200"
    >
      no change
    </span>
  );
}

export default function IntelligenceVerdictsTable({ rows, criteriaByDeal }) {
  if (!rows || rows.length === 0) return null;
  // Pre-sort: biggest improvements first so the page hero feels like a leaderboard.
  const sorted = [...rows].sort(
    (a, b) =>
      (b.at_counter?.verdict_improved_grades ?? 0) -
      (a.at_counter?.verdict_improved_grades ?? 0)
  );
  // Batch H — surface the criteria pass-count chip when the parent has
  // pre-fetched per-deal criteria-eval summaries. Hidden gracefully when
  // no rules are enabled (criteriaByDeal[id].evaluated_count === 0).
  const hasCriteriaCol = !!(criteriaByDeal && Object.keys(criteriaByDeal).length);
  return (
    <div
      data-testid="verdicts-view"
      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2 text-left font-semibold">Property</th>
            <th className="px-4 py-2 text-left font-semibold">@ Asking</th>
            <th className="px-4 py-2 text-left font-semibold">@ Your counter</th>
            {/* Phase 36 Batch Q — payback at the user's counter (or
                asking, when the role is seller). Position chosen so it
                sits next to the verdict comparison; readers' eye flow
                goes "verdict shifted ↑ · pays back in 8 yrs". */}
            <th className="px-4 py-2 text-right font-semibold">Payback</th>
            {hasCriteriaCol ? (
              <th className="px-4 py-2 text-right font-semibold">Criteria</th>
            ) : null}
            <th className="px-4 py-2 text-right font-semibold">Shift</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const d = r.deal;
            const isSeller = (d?.role || "buyer").toLowerCase() === "seller";
            const vAsk = r.at_asking?.verdict || "Hold";
            const vCnt = r.at_counter?.verdict || "Hold";
            const grade = r.at_counter?.verdict_improved_grades ?? 0;
            const crit = criteriaByDeal?.[d.id];
            return (
              <tr
                key={d.id}
                data-testid={`verdicts-row-${d.id}`}
                className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/40"
              >
                <td className="px-4 py-3 align-top">
                  <div className="flex items-center gap-2">
                    {/* Phase 36 Batch L — role indicator dot per row */}
                    <RoleDot
                      metrics={d.metrics_at_role}
                      role={d.role}
                      testid={`verdicts-role-dot-${d.id}`}
                    />
                    <span className="truncate text-sm font-medium text-slate-900">
                      {d.property?.address || "—"}
                    </span>
                    {d.pipeline_stage ? (
                      <StageBadge
                        stage={d.pipeline_stage}
                        dealId={d.id}
                        editable
                        size="sm"
                      />
                    ) : null}
                  </div>
                  <div className="ml-3.5 text-xs text-slate-500">
                    {d.property?.city ? `${d.property.city}, ${d.property.state}` : "—"}
                  </div>
                </td>
                <td className="px-4 py-3 align-top">
                  <VerdictBadge verdict={vAsk} />
                </td>
                <td className="px-4 py-3 align-top">
                  {isSeller ? (
                    /* Seller view — counter is the buyer's signal, so the
                       "@ your counter" framing doesn't apply. Show a muted
                       caption instead of a fake verdict comparison. */
                    <span
                      data-testid={`verdicts-seller-na-${d.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 ring-1 ring-amber-200"
                    >
                      Seller view · n/a
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                      <VerdictBadge verdict={vCnt} />
                    </span>
                  )}
                </td>
                {/* Phase 36 Batch Q — Payback at counter (buyer) or
                    asking (seller). Falls back to deal.metrics_at_role
                    when the per-price recompute didn't run (legacy
                    rows). Null → muted "Never". */}
                <td className="px-4 py-3 text-right align-top" data-testid={`verdicts-payback-${d.id}`}>
                  {(() => {
                    const counterPayback = r.at_counter?.metrics?.payback_period_years;
                    const askingPayback = r.at_asking?.metrics?.payback_period_years;
                    const fallback = d.metrics_at_role?.payback_period_years;
                    const pp = isSeller
                      ? (askingPayback ?? fallback ?? null)
                      : (counterPayback ?? askingPayback ?? fallback ?? null);
                    if (pp == null) {
                      return <span className="text-xs text-slate-400">Never</span>;
                    }
                    return (
                      <span className="tabular-nums text-sm font-medium text-slate-700">
                        {Number(pp).toFixed(1)}
                        <span className="ml-0.5 text-[10px] text-slate-400">yrs</span>
                      </span>
                    );
                  })()}
                </td>
                {hasCriteriaCol ? (
                  <td className="px-4 py-3 text-right align-top">
                    {crit && crit.evaluated_count > 0 ? (
                      <a
                        href={`/deals/${d.id}#criteria`}
                        data-testid={`verdicts-criteria-${d.id}`}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ring-1 ${
                          crit.failed_count === 0
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100"
                            : crit.passed_count === 0
                              ? "bg-rose-50 text-rose-700 ring-rose-200 hover:bg-rose-100"
                              : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100"
                        }`}
                        title={
                          crit.top_failures?.length
                            ? `Top failures: ${crit.top_failures.join(", ")}`
                            : "All enabled criteria pass"
                        }
                      >
                        {crit.passed_count}/{crit.evaluated_count} pass
                      </a>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </td>
                ) : null}
                <td className="px-4 py-3 text-right align-top">
                  {isSeller ? (
                    <span className="text-[10px] text-slate-300">—</span>
                  ) : (
                    <ShiftPill grade={grade} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
